import React, { useMemo, useState } from 'react';
import KpiRow from '../ar/ArKpiRow';
import { TRADES, STANDINGS, coiState, jobDocNeeds, focusOfNeed, todayYmd, fmtMonthYear } from '../../lib/subcontractors';

/* Subcontractor Mgmt's main screen (Duncan 2026-10-21: "a dashboard that shows specs ... about the
   subcontractors", then "a better design that matches our really nice pages like job cost"): AR's KPI band,
   a document-compliance card in the look of AR's aging bar, subs by trade and subs used each year, and the
   subs used in the last two years whose required documents aren't all in order -- as Job Costs-style cards.
   Duncan 2026-10-22: a company owes its MSA and W-9 once; every sub on every open job owes that job a certificate
   of insurance and a job site safety plan -- counted here per sub per job, and listed under "Job documents
   missing" (a row opens the job's Subcontractors tab, where they're added).
   Duncan 2026-10-22: a flag goes to "the spot where to file" it -- an MSA or W-9 pill to the sub's row on its
   Documents tab, a job's missing paper to that sub's card on the job, at its row. */

const RECENT_DAYS = 730;
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const pct = (n, of) => (of ? Math.round((n / of) * 100) : 0);
// A share's colour: in order (90%+), getting there (50%+), or not.
const shareTone = (share) => (share >= 90 ? 'var(--success)' : share >= 50 ? 'var(--warning)' : 'var(--danger)');
const OK = 'var(--success)';
const SOON = 'var(--warning)';
const BAD = 'var(--danger)';
const NONE = 'color-mix(in oklch, var(--text-4) 55%, var(--bg-surface))';

const cardStyle = { background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' };
const cardTitle = { fontSize: 12.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' };

/* A sub's company documents, once each (Duncan 2026-10-22): on file or missing. */
function docStates(s) {
  return [
    { key: 'msa', label: 'MSA', state: s.msa ? 'ok' : 'bad' },
    { key: 'w9', label: 'W-9', state: s.w9 ? 'ok' : 'bad' },
  ];
}

/* Each sub on each open job: where its certificate for that job stands (in force, lapsing, lapsed or not naming
   the job, none) and whether its safety plan is on file. */
function jobCounts(jobDocs, today) {
  const coi = { current: 0, expiring: 0, expired: 0, none: 0 };
  let safety = 0;
  jobDocs.forEach((j) => {
    const level = coiState(j.documents, today).level;
    coi[level === 'unnamed' ? 'expired' : level] += 1;
    if (j.documents.some((d) => d.doc_type === 'safety')) safety += 1;
  });
  return { coi, safety, total: jobDocs.length };
}

/* A stacked bar in AR's aging look: its parts' counts inside them when they fit, every part's on hover. */
const StackBar = ({ parts, total }) => {
  const [hover, setHover] = useState(null);
  const shown = parts.filter((p) => p.n > 0);
  return (
    <div style={{ display: 'flex', gap: 2, height: 24, borderRadius: 7, overflow: 'visible', background: 'var(--bg-surface-2)' }}>
      {shown.map((p, i) => {
        const share = (p.n / total) * 100;
        return (
          <div key={p.key} onMouseEnter={() => setHover(p.key)} onMouseLeave={() => setHover(null)}
            style={{
              width: `${share}%`, minWidth: 3, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: p.tone, cursor: 'default',
              borderRadius: `${i === 0 ? 7 : 0}px ${i === shown.length - 1 ? 7 : 0}px ${i === shown.length - 1 ? 7 : 0}px ${i === 0 ? 7 : 0}px`,
            }}>
            {share >= 14 && (
              <span className="mono" style={{ fontSize: 12.5, fontWeight: 700, color: p.ink || '#fff', whiteSpace: 'nowrap', padding: '0 6px', textShadow: p.ink ? 'none' : '0 1px 1px oklch(0 0 0 / 0.18)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {p.n} {p.text}
              </span>
            )}
            {hover === p.key && (
              <div role="tooltip" style={{ position: 'absolute', bottom: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)', zIndex: 20, whiteSpace: 'nowrap', pointerEvents: 'none', background: 'var(--text-1)', color: 'var(--bg-surface)', borderRadius: 7, padding: '6px 10px', boxShadow: 'var(--shadow-md)' }}>
                <span className="mono" style={{ fontSize: 13.5, fontWeight: 700 }}>{p.n}</span>
                <span style={{ fontSize: 12.5, marginLeft: 6, opacity: 0.85 }}>{p.text} · {pct(p.n, total)}%</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const ComplianceCard = ({ subs, jobDocs, today }) => {
  const total = subs.length || 1;
  const has = (k) => subs.filter((s) => s[k]).length;
  const jobs = jobCounts(jobDocs, today);
  const rows = [
    { label: 'MSA', total, parts: [{ key: 'ok', n: has('msa'), text: 'on file', tone: OK }, { key: 'bad', n: total - has('msa'), text: 'missing', tone: BAD }] },
    { label: 'W-9', total, parts: [{ key: 'ok', n: has('w9'), text: 'on file', tone: OK }, { key: 'bad', n: total - has('w9'), text: 'missing', tone: BAD }] },
    {
      label: 'Job COIs', total: jobs.total,
      parts: [
        { key: 'ok', n: jobs.coi.current, text: 'in force', tone: OK }, { key: 'soon', n: jobs.coi.expiring, text: 'expiring', tone: SOON },
        { key: 'bad', n: jobs.coi.expired, text: 'expired or not naming the job', tone: BAD }, { key: 'none', n: jobs.coi.none, text: 'No COI Recorded', tone: NONE, ink: 'var(--text-2)' },
      ],
    },
    { label: 'Job safety plans', total: jobs.total, parts: [{ key: 'ok', n: jobs.safety, text: 'on file', tone: OK }, { key: 'bad', n: jobs.total - jobs.safety, text: 'missing', tone: BAD }] },
  ];
  const legend = [['On file / in force', OK], ['Expiring in 30 days', SOON], ['Missing / expired', BAD], ['No COI Recorded', NONE]];
  return (
    <section style={{ ...cardStyle, display: 'flex', flexDirection: 'column', gap: 12, padding: '16px 18px' }} aria-label="Document compliance">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={cardTitle}>Document compliance</span>
        <span style={{ fontSize: 12.5, color: 'var(--text-4)' }}>MSA and W-9 once per company · a COI and a safety plan for each sub on each open job ({jobs.total})</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '120px minmax(0, 1fr)', gap: '9px 14px', alignItems: 'center' }}>
        {rows.map((r) => (
          <React.Fragment key={r.label}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-2)' }}>{r.label}</span>
            {r.total ? <StackBar parts={r.parts} total={r.total} /> : <span style={{ fontSize: 13, color: 'var(--text-4)' }}>No subs on an open job yet</span>}
          </React.Fragment>
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 22px' }}>
        {legend.map(([text, tone]) => (
          <span key={text} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--text-3)' }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: tone }} />{text}
          </span>
        ))}
      </div>
    </section>
  );
};

/* A hover label inside a chart's card. */
const useTip = () => {
  const [tip, setTip] = useState(null);
  const bind = (text) => ({
    onMouseEnter: (e) => {
      const box = e.currentTarget.closest('[data-chart]').getBoundingClientRect();
      const r = e.currentTarget.getBoundingClientRect();
      setTip({ text, x: r.left + r.width / 2 - box.left, y: r.top - box.top });
    },
    onMouseLeave: () => setTip(null),
  });
  const node = tip && (
    <div role="tooltip" style={{ position: 'absolute', left: tip.x, top: tip.y - 8, transform: 'translate(-50%, -100%)', pointerEvents: 'none', padding: '5px 10px', borderRadius: 7, whiteSpace: 'nowrap', fontSize: 12.5, fontWeight: 600, color: 'var(--bg-surface)', background: 'var(--text-1)', boxShadow: 'var(--shadow-md)', zIndex: 2 }}>{tip.text}</div>
  );
  return [bind, node];
};

const ByTrade = ({ subs }) => {
  const [bind, tip] = useTip();
  const rows = [
    ...TRADES.map((t) => ({ name: t, n: subs.filter((s) => s.trades.includes(t)).length })).filter((r) => r.n > 0).sort((a, b) => b.n - a.n),
    { name: 'No trade set', n: subs.filter((s) => !s.trades.length).length, none: true },
  ].filter((r) => r.n > 0);
  const max = Math.max(1, ...rows.map((r) => r.n));
  return (
    <section data-chart style={{ ...cardStyle, padding: '16px 18px', position: 'relative', minWidth: 0 }} aria-label="Subs by trade">
      <div style={{ ...cardTitle, marginBottom: 12 }}>Subs by trade</div>
      <div style={{ display: 'grid', gridTemplateColumns: '96px minmax(0, 1fr) 34px', gap: '7px 12px', alignItems: 'center' }}>
        {rows.map((r) => (
          <React.Fragment key={r.name}>
            <span style={{ fontSize: 13, fontWeight: 500, color: r.none ? 'var(--text-3)' : 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</span>
            <span {...bind(`${r.name}: ${plural(r.n, 'sub')}`)} style={{ height: 16, display: 'flex', alignItems: 'center', cursor: 'default' }}>
              <span style={{ height: 12, width: `${Math.max(2, (r.n / max) * 100)}%`, borderRadius: '0 4px 4px 0', background: r.none ? NONE : 'var(--accent)' }} />
            </span>
            <span className="mono" style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.n}</span>
          </React.Fragment>
        ))}
      </div>
      {tip}
    </section>
  );
};

const ByYear = ({ usage }) => {
  const [bind, tip] = useTip();
  const thisYear = new Date().getFullYear();
  const years = Array.from({ length: 8 }, (_, i) => String(thisYear - 7 + i));
  const byYear = new Map((usage || []).map((u) => [String(u.year), u.subs]));
  const max = Math.max(1, ...years.map((y) => byYear.get(y) || 0));
  return (
    <section data-chart style={{ ...cardStyle, padding: '16px 18px', position: 'relative', minWidth: 0 }} aria-label="Subs used each year">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={cardTitle}>Subs used each year</span>
        <span style={{ fontSize: 12.5, color: 'var(--text-4)' }}>with a CE subcontract dated that year</span>
      </div>
      <div style={{ position: 'relative', height: 132 }}>
        {[0.5, 1].map((g) => <div key={g} aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, bottom: `${g * 100}%`, borderTop: '1px dashed var(--border-subtle)' }} />)}
        <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: `repeat(${years.length}, minmax(0, 1fr))`, gap: 2, alignItems: 'end', borderBottom: '1px solid var(--border-default)' }}>
          {years.map((y) => {
            const n = byYear.get(y) || 0;
            return (
              <span key={y} {...bind(`${y}: ${plural(n, 'sub')}`)} style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', cursor: 'default' }}>
                <span className="mono" style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-2)', marginBottom: 3 }}>{n || ''}</span>
                <span style={{ width: '58%', height: `${n ? Math.max(3, (n / max) * 82) : 0}%`, borderRadius: '4px 4px 0 0', background: 'var(--accent)' }} />
              </span>
            );
          })}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${years.length}, minmax(0, 1fr))`, gap: 2, marginTop: 5 }}>
        {years.map((y) => <span key={y} className="mono" style={{ fontSize: 12, color: 'var(--text-3)', textAlign: 'center' }}>{y}</span>)}
      </div>
      {tip}
    </section>
  );
};

const PILL = {
  ok: { color: 'var(--success)', bg: 'var(--success-soft)' },
  soon: { color: 'var(--warning)', bg: 'var(--warning-soft)' },
  bad: { color: 'var(--danger)', bg: 'var(--danger-soft)' },
};

/* One sub that needs attention, in Job Costs' card look: who, its company documents, how many are in order. */
const AttentionCard = ({ sub, docs, onOpen }) => {
  const inOrder = docs.filter((d) => d.state === 'ok').length;
  const tone = inOrder === docs.length ? OK : inOrder > 0 ? SOON : BAD;
  const meta = [sub.trades.length ? sub.trades.join(', ') : 'No trade set', plural(sub.jobs, 'job'),
    sub.on_job_now ? 'on a job now' : sub.last_job ? `last ${fmtMonthYear(sub.last_job)}` : sub.next_job ? `next ${fmtMonthYear(sub.next_job)}` : 'no jobs yet'];
  return (
    <article className="sub-row" role="button" tabIndex={0} aria-label={`Open ${sub.name}`}
      onClick={() => onOpen(sub)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(sub); }}
      style={{ ...cardStyle, cursor: 'pointer', overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1.6fr) auto', gap: 18, alignItems: 'center', padding: '13px 18px 11px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <span style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.012em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub.name}</span>
          <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{meta.join(' · ')}</span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {docs.map((d) => (
            <button key={d.key} type="button" title={`${d.label}: show where it’s filed`} onClick={(e) => { e.stopPropagation(); onOpen(sub, d.key); }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap', cursor: 'pointer', color: PILL[d.state].color, background: PILL[d.state].bg, border: `1px solid color-mix(in oklch, ${PILL[d.state].color} 22%, transparent)` }}>
              {d.label}{d.note && <span style={{ fontWeight: 500 }}>{d.note}</span>}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)' }}>In order</span>
          <span className="mono" style={{ fontSize: 18, fontWeight: 700, color: tone, lineHeight: 1.1 }}>{inOrder}<span style={{ fontSize: 13, color: 'var(--text-3)', fontWeight: 600 }}> of {docs.length}</span></span>
        </div>
      </div>
      <div aria-hidden="true" style={{ display: 'grid', gridTemplateColumns: `repeat(${docs.length}, minmax(0, 1fr))`, gap: 2, height: 4, margin: '0 18px 12px' }}>
        {docs.map((d) => <span key={d.key} style={{ borderRadius: 2, background: d.state === 'ok' ? OK : d.state === 'soon' ? SOON : 'var(--bg-surface-2)' }} />)}
      </div>
    </article>
  );
};

const NeedsAttention = ({ subs, onOpen, since }) => {
  const [all, setAll] = useState(false);
  const rows = useMemo(() => subs
    .filter((s) => s.latest_work && s.latest_work >= since) // any work, still to come too (as before 10-24)
    .map((s) => ({ sub: s, docs: docStates(s) }))
    .filter((r) => r.docs.some((d) => d.state !== 'ok'))
    .sort((a, b) => b.sub.latest_work.localeCompare(a.sub.latest_work) || a.sub.name.localeCompare(b.sub.name)), [subs, since]);
  const shown = all ? rows : rows.slice(0, 10);
  return (
    <section aria-label="Needs attention" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', margin: '4px 2px 0' }}>
        <span style={{ fontSize: 13.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Needs attention</span>
        <span className="mono" style={{ minWidth: 22, height: 20, padding: '0 7px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: rows.length ? 'var(--danger)' : 'var(--text-3)', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>{rows.length}</span>
        <span style={{ fontSize: 13, color: 'var(--text-3)' }}>subs used since {fmtMonthYear(since)} with their MSA or W-9 missing</span>
      </div>
      {rows.length === 0 ? (
        <div style={{ ...cardStyle, padding: '16px 18px', fontSize: 13.5, color: 'var(--text-3)' }}>Every sub used since {fmtMonthYear(since)} has its MSA and W-9 on file.</div>
      ) : shown.map((r) => <AttentionCard key={r.sub.id} sub={r.sub} docs={r.docs} onOpen={onOpen} />)}
      {rows.length > 10 && (
        <button type="button" onClick={() => setAll((v) => !v)} style={{ alignSelf: 'center', padding: '6px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600, color: 'var(--accent-text)', background: 'var(--accent-soft)', border: '1px solid color-mix(in oklch, var(--accent) 25%, transparent)', cursor: 'pointer' }}>
          {all ? 'Show fewer' : `Show all ${rows.length}`}
        </button>
      )}
    </section>
  );
};

/* Every sub on every open job still owing that job its certificate of insurance or safety plan (or with a
   certificate to follow up), job by job. A row opens the job's Subcontractors tab, where they're added. */
const JOB_COLS = 'minmax(0, 1.2fr) minmax(0, 1.7fr) minmax(0, 0.9fr) minmax(0, 1.6fr)';
const JobDocsMissing = ({ subs, jobDocs, today, onOpenJob }) => {
  const [all, setAll] = useState(false);
  const rows = useMemo(() => {
    const name = new Map(subs.map((s) => [s.id, s.name]));
    return jobDocs.map((j) => ({ ...j, sub: name.get(j.subcontractor_id) || 'A subcontractor', needs: jobDocNeeds(j.documents, today) }))
      .filter((r) => r.needs.length)
      .sort((a, b) => String(a.project_name).localeCompare(String(b.project_name)) || a.sub.localeCompare(b.sub));
  }, [subs, jobDocs, today]);
  const shown = all ? rows : rows.slice(0, 10);
  return (
    <section aria-label="Job documents missing" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', margin: '4px 2px 0' }}>
        <span style={{ fontSize: 13.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Job documents missing</span>
        <span className="mono" style={{ minWidth: 22, height: 20, padding: '0 7px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: rows.length ? 'var(--danger)' : 'var(--text-3)', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>{rows.length}</span>
        <span style={{ fontSize: 13, color: 'var(--text-3)' }}>every sub on every open job needs that job’s COI and job site safety plan</span>
      </div>
      {rows.length === 0 ? (
        <div style={{ ...cardStyle, padding: '16px 18px', fontSize: 13.5, color: 'var(--text-3)' }}>
          {jobDocs.length ? 'Every sub on an open job has that job’s COI and safety plan.' : 'No subs on an open job yet.'}
        </div>
      ) : (
        <div style={{ ...cardStyle, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: JOB_COLS, gap: 14, padding: '9px 18px', background: 'var(--bg-surface-2)', borderBottom: '1px solid var(--border-default)' }}>
            {['Sub', 'Job', 'PM', 'Missing'].map((h) => <span key={h} style={{ ...cardTitle, fontSize: 11.5 }}>{h}</span>)}
          </div>
          {shown.map((r) => (
            <div key={`${r.subcontractor_id}:${r.project_id}`} className="sub-row" role="button" tabIndex={0} aria-label={`Open ${r.project_name}'s subcontractors`}
              onClick={() => onOpenJob(r, focusOfNeed(r.needs[0].key))} onKeyDown={(e) => { if (e.key === 'Enter') onOpenJob(r, focusOfNeed(r.needs[0].key)); }}
              style={{ display: 'grid', gridTemplateColumns: JOB_COLS, gap: 14, alignItems: 'center', padding: '10px 18px', borderTop: '1px solid var(--border-subtle)', cursor: 'pointer', fontSize: 13.5 }}>
              <span style={{ fontWeight: 600, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.sub}</span>
              <span style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.project_number && <span className="mono" style={{ color: 'var(--text-3)' }}>{r.project_number} </span>}{r.project_name}
                </span>
                {r.is_test && <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--warning)', padding: '0 6px', borderRadius: 999, border: '1px solid color-mix(in oklch, var(--warning) 40%, transparent)', whiteSpace: 'nowrap' }}>Test</span>}
              </span>
              <span style={{ color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.project_manager || '—'}</span>
              <span style={{ fontSize: 13 }}>
                {r.needs.map((n, i) => (
                  <React.Fragment key={n.key}>
                    {i > 0 && <span style={{ color: 'var(--text-4)' }}> · </span>}
                    <button type="button" title={n.title || 'Show where it’s filed'} onClick={(e) => { e.stopPropagation(); onOpenJob(r, focusOfNeed(n.key)); }}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: n.tone, fontWeight: 600 }}>{n.text}</button>
                  </React.Fragment>
                ))}
              </span>
            </div>
          ))}
        </div>
      )}
      {rows.length > 10 && (
        <button type="button" onClick={() => setAll((v) => !v)} style={{ alignSelf: 'center', padding: '6px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600, color: 'var(--accent-text)', background: 'var(--accent-soft)', border: '1px solid color-mix(in oklch, var(--accent) 25%, transparent)', cursor: 'pointer' }}>
          {all ? 'Show fewer' : `Show all ${rows.length}`}
        </button>
      )}
    </section>
  );
};

const SubDashboard = ({ subs, usage, jobDocs = [], onOpen, onOpenJob }) => {
  const today = todayYmd();
  const since = new Date(Date.parse(`${today}T00:00:00Z`) - RECENT_DAYS * 864e5).toISOString().slice(0, 10);
  const total = subs.length;
  const count = (f) => subs.filter(f).length;
  const msa = count((s) => s.msa);
  const w9 = count((s) => s.w9);
  const jobs = jobCounts(jobDocs, today);
  const coiOk = jobs.coi.current + jobs.coi.expiring;
  const recent = count((s) => s.latest_work && s.latest_work >= since);
  const standing = STANDINGS.map((st) => ({ n: count((s) => s.standing === st.value), text: st.label.toLowerCase() })).filter((p) => p.n);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <style>{'@media (max-width: 1000px) { .subdash-charts { grid-template-columns: minmax(0, 1fr) !important; } }'}</style>
      <KpiRow label="Subcontractor totals" tiles={[
        { label: 'Subcontractors', value: total, sub: standing.map((p) => `${p.n} ${p.text}`).join(' · ') },
        { label: 'MSA on file', value: `${pct(msa, total)}%`, valueColor: shareTone(pct(msa, total)), sub: `${msa} of ${total} · ${total - msa} missing` },
        { label: 'W-9 on file', value: `${pct(w9, total)}%`, valueColor: shareTone(pct(w9, total)), sub: `${w9} of ${total}` },
        {
          label: 'Job COIs in force', value: jobs.total ? `${coiOk} of ${jobs.total}` : '—', valueColor: jobs.total ? shareTone(pct(coiOk, jobs.total)) : undefined,
          sub: jobs.total ? `${jobs.coi.none} No COI Recorded · ${jobs.coi.expired} expired or not naming the job` : 'no subs on an open job',
        },
        { label: `Used since ${fmtMonthYear(since)}`, value: recent, sub: 'subs with a job in 2 years' },
      ]} />
      <ComplianceCard subs={subs} jobDocs={jobDocs} today={today} />
      <div className="subdash-charts" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }}>
        <ByTrade subs={subs} />
        <ByYear usage={usage} />
      </div>
      <JobDocsMissing subs={subs} jobDocs={jobDocs} today={today} onOpenJob={onOpenJob} />
      <NeedsAttention subs={subs} onOpen={onOpen} since={since} />
    </div>
  );
};

export default SubDashboard;
